// MA-840 slice 2a — spoken CUE hints for the math families whose every
// picture rung either repaints the stem picture or prints the answer.
//
// Founder 2026-10-07 (ME_13_020 "How many ones are with the ten?" offered no
// hint): a hint is owed wherever the logic allows one. For these families the
// one help that names no number and draws nothing is a short spoken process
// cue ("The tall sticks are tens. The little cubes are ones."). Operator
// ruling D-1008-25: the cue lives in its OWN field on the mapped item,
// `mathCue: { templateId, text }` — never in `supports` and never as a
// scaffold, so the item's shape, supports and scaffold are byte-identical to
// before. HintSlot renders it as the SEN rung (ScaffoldSEN speaks and prints
// it, the MA-595 families-b way) only where the ladder would otherwise be
// empty. Cues are family TEMPLATES (one string per family, never per item),
// so the spoken-string count stays small.
//
// Two rules keep a cue honest:
//   1. Answer-safety: `cueNamesAnswer` — a cue never contains the keyed
//      answer as a digit string or as a number word (one..ninety, hundred).
//      An item whose template would name its answer gets NO cue (fail-closed).
//   2. No repaint: the cue draws nothing (ScaffoldSEN is text + speaker), it
//      never enters the stem/picture filters, and HintSlot's
//      `resolveScaffoldLadder` offers it ONLY when the ladder would otherwise
//      be empty (no structural rung survives and no SHOW applies), so it never
//      adds a tab beside a picture rung.

import type { SupportCode } from "@/types/engine";

/** The family templates. Short, decodable instructions; no digits and no
 *  number word that can equal a key (checked per served item by the test). */
export const MATH_HINT_CUES = Object.freeze({
  "count-frame": "Touch each thing as you count it. Say a number for each thing.",
  "quick-look": "Look for small groups you know. Put them together.",
  compare: "Count each group. Then compare the numbers.",
  "missing-part": "Start at the part you know. Count up to the whole.",
  "pv-blocks": "The tall sticks are tens. The little cubes are ones.",
  "teen-frames": "A full frame is ten. Count the dots in the other frame.",
  "fraction-bar": "Count all the parts. Then count the shaded parts.",
  coins: "Start with the coin worth the most. Count on for each coin.",
  clock: "The short hand shows the hour. The long hand shows the minutes.",
  "skip-line": "Find how much it jumps each time. Keep jumping by that much.",
  "equal-groups": "Skip count by the size of each group or row.",
  sharing: "Share them out into equal groups. Then count.",
  "build-number": "Tens make the first digit. Ones make the last digit.",
  "compare-numbers": "Look at the tens first. More tens means a bigger number.",
  "compare-fractions": "Picture each fraction as part of a bar. Compare how much is filled.",
  // MA-987 — odd and even by pairing (ms.math.29, picture-free). No number
  // word, so the cue can never name a key (0 or 1 on the leftover items).
  pairs: "Put them in pairs. Even numbers have no leftover. Odd numbers have a leftover.",
} as const);

export type MathCueFamily = keyof typeof MATH_HINT_CUES;

/** The mapped item's cue field (D-1008-25). */
export interface MathCue {
  templateId: MathCueFamily;
  text: string;
}

type CueItem = {
  supports?: SupportCode[];
  mathCue?: MathCue;
  answer?: unknown;
  mathVisual?: { kind?: string } | null;
  visualScaffold?: { kind?: string; data?: unknown } | null;
};

const NUMBER_WORDS: Record<number, string> = {
  0: "zero", 1: "one", 2: "two", 3: "three", 4: "four", 5: "five", 6: "six", 7: "seven", 8: "eight", 9: "nine",
  10: "ten", 11: "eleven", 12: "twelve", 13: "thirteen", 14: "fourteen", 15: "fifteen", 16: "sixteen",
  17: "seventeen", 18: "eighteen", 19: "nineteen", 20: "twenty", 30: "thirty", 40: "forty", 50: "fifty",
  60: "sixty", 70: "seventy", 80: "eighty", 90: "ninety", 100: "hundred",
};

/** Every number the keyed answer states: an integer key, the parts of a
 *  fraction ("3/4"), or the hour/minutes of a time ("2:30"). */
function answerNumbers(answer: unknown): number[] {
  return (String(answer ?? "").match(/\d+/g) ?? []).map(Number);
}

/** True when `cue` names a number the keyed answer states — as digits or as
 *  a whole number word (word-bounded: "tens"/"ones" are unit names, not
 *  "ten"/"one"). Exported: the MA-840 guard runs it over every served item. */
export function cueNamesAnswer(cue: string, answer: unknown): boolean {
  const text = String(cue);
  for (const n of answerNumbers(answer)) {
    if (new RegExp(`(^|\\D)${n}(\\D|$)`).test(text)) return true;
    const w = NUMBER_WORDS[n];
    if (w && new RegExp(`\\b${w}\\b`, "i").test(text)) return true;
    // a compound key (e.g. 25) is spoken "twenty-five": its tens word counts
    if (n > 20 && n < 100) {
      const tens = NUMBER_WORDS[Math.floor(n / 10) * 10];
      const ones = NUMBER_WORDS[n % 10];
      if (tens && ones && new RegExp(`\\b${tens}[- ]${ones}\\b`, "i").test(text)) return true;
    }
  }
  return false;
}

const COUNT_SKILLS = new Set(["ms.math.00a", "ms.math.00b"]);
const BOND_SKILLS = new Set(["ms.math.05", "ms.math.06", "ms.math.07", "ms.math.09", "ms.math.12"]);

/** The cue family for a mapped math item on `skillId`, or null (no cue). */
export function mathHintCueFamily(item: CueItem, skillId: string): MathCueFamily | null {
  const mv = item.mathVisual?.kind;
  const vs = item.visualScaffold?.kind;
  const sup = item.supports ?? [];
  // counting frames whose only rung (F5/T10) is the frame on screen
  if (COUNT_SKILLS.has(skillId) && mv === "ten_frame" && vs === "count") return "count-frame";
  // subitizing dot cards (a "count" rung that repaints the card)
  if (skillId === "ms.math.01" && mv === "dot_pattern" && vs === "count") return "quick-look";
  // the compare pictures, whose only rung IS the picture on screen
  if (skillId === "ms.math.03" && (vs === "compare" || vs === "compare3" || vs === "compare_ten_frames")) return "compare";
  // the number-bond missing part, whose NB rung is the bond on screen
  if (BOND_SKILLS.has(skillId) && vs === "missing_addend" && sup.includes("NB")) return "missing-part";
  if ((skillId === "ms.math.13" || skillId === "ms.math.20") && mv === "place_value_blocks") return "pv-blocks";
  if (skillId === "ms.math.13" && mv === "two_ten_frames_teen") return "teen-frames";
  if (skillId === "ms.math.22" && mv === "fraction_bar") return "fraction-bar";
  if (skillId === "ms.math.24" && mv === "coins") return "coins";
  if (skillId === "ms.math.25" && mv === "clock") return "clock";
  if (skillId === "ms.math.16" && mv === "skip_count_line") return "skip-line";
  if (skillId === "ms.math.20" && mv === "place_value_comparison") return "compare-numbers";
  if ((skillId === "ms.math.23" || skillId === "ms.math.28") && mv === "fraction_comparison") return "compare-fractions";
  // word problems and their pictures
  if (skillId === "ms.math.17" && (mv === "discrete_clusters" || !mv) && !vs) return "equal-groups";
  if (skillId === "ms.math.18" && mv === "array" && !vs) return "equal-groups";
  if (skillId === "ms.math.27" && !mv && !vs) return "equal-groups";
  if (skillId === "ms.math.19" && (mv === "division_sharing" || !mv) && !vs) return "sharing";
  if (skillId === "ms.math.21" && !mv && !vs) return "build-number";
  // MA-987 — every ms.math.29 item is picture-free (no mathVisual, no scaffold).
  if (skillId === "ms.math.29" && !mv && !vs) return "pairs";
  return null;
}

/** The ScaffoldSEN payload for an item's math cue (what HintSlot renders). */
export function mathCueScaffoldData(item: object): { hintCue: string } | undefined {
  const cue = (item as { mathCue?: MathCue | null }).mathCue;
  return cue?.text ? { hintCue: cue.text } : undefined;
}

/** Adds the family cue to a mapped math item as `mathCue` (fail-closed: no
 *  family, an item already carrying SEN, or a cue that would name the key →
 *  unchanged). Nothing else on the item changes. */
export function withMathHintCue<T extends CueItem>(item: T, skillId: string): T {
  const family = mathHintCueFamily(item, skillId);
  if (!family) return item;
  if ((item.supports ?? []).includes("SEN")) return item;
  const text = MATH_HINT_CUES[family];
  if (cueNamesAnswer(text, item.answer)) return item;
  return { ...item, mathCue: { templateId: family, text } };
}
